import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router";

import {
  PlayerRole,
  setFilterValues,
  updateFilter,
} from "../redux/players/playersSlice";
import { AppDispatch } from "../redux/store";

interface UseUrlFiltersProps {
  playerFilters: any[];
  sort: string;
  ownedOrEvolvedFilters: any;
  stableQueryParams: any[];
  playerRole?: PlayerRole | "best";
  playerRoleIds?: number[];
  readyToSync?: boolean;
}

export const useUrlFilters = ({
  playerFilters,
  sort,
  ownedOrEvolvedFilters,
  stableQueryParams,
  playerRole,
  playerRoleIds,
  readyToSync = true,
}: UseUrlFiltersProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasInitializedFromUrl, setHasInitializedFromUrl] = useState(false);
  const initialPlayerFiltersRef = useRef(playerFilters);

  useEffect(() => {
    initialPlayerFiltersRef.current = playerFilters;
  }, [playerFilters]);

  // Initialize Redux filters from URL on mount
  useEffect(() => {
    if (hasInitializedFromUrl) return;

    const urlOwnedOrEvolved = searchParams.get("ownedOrEvolved");
    const urlActiveSquad = searchParams.has("activeSquad");

    /*
      Set, not toggled: the filters outlive the page in the store, so on a
      second visit a toggle would switch off what the link asks for.
    */
    if (urlActiveSquad) {
      dispatch(setFilterValues({ key: "ownedOrEvolved", values: [2] }));
    } else if (urlOwnedOrEvolved) {
      const filterValue = urlOwnedOrEvolved === "Owned" ? 0 : 1;
      dispatch(
        setFilterValues({ key: "ownedOrEvolved", values: [filterValue] }),
      );
    }

    initialPlayerFiltersRef.current.forEach((filter) => {
      if (filter.type === "RANGE_FILTER") {
        const directValue = searchParams.get(filter.key);
        const minValue = searchParams.get(`${filter.key}Min`);
        const maxValue = searchParams.get(`${filter.key}Max`);

        if (directValue && !minValue && !maxValue) {
          dispatch(
            updateFilter({ key: filter.key, max: parseInt(directValue) }),
          );
        } else if (minValue || maxValue) {
          if (minValue)
            dispatch(
              updateFilter({ key: filter.key, min: parseInt(minValue) }),
            );
          if (maxValue)
            dispatch(
              updateFilter({ key: filter.key, max: parseInt(maxValue) }),
            );
        }
      } else if (
        filter.type === "SELECTOR_FILTER" &&
        filter.key !== "ownedOrEvolved"
      ) {
        const urlValue = searchParams.get(filter.key);
        if (urlValue) {
          const values = urlValue
            .split(",")
            .map((value) =>
              filter.useLabelAsId
                ? filter.options.find((o) => o.label === value)?.id
                : parseInt(value),
            )
            .filter((id): id is number => id != null && !isNaN(id));
          dispatch(setFilterValues({ key: filter.key, values }));
        }
      }
    });

    setHasInitializedFromUrl(true);
  }, [searchParams, dispatch, hasInitializedFromUrl]);

  // Sync state back to URL
  useEffect(() => {
    if (!hasInitializedFromUrl || !readyToSync) return;

    const newSearchParams = new URLSearchParams();
    let hasActiveSquad = false;

    if (playerRole && playerRole !== "best") {
      newSearchParams.set("playerRoleId", playerRole.id.toString());
    }
    if (playerRoleIds?.length > 0) {
      newSearchParams.set(
        "player-role-ids",
        [...playerRoleIds].sort((a, b) => a - b).join(","),
      );
    }

    if (sort) newSearchParams.set("sort", sort);

    if (ownedOrEvolvedFilters?.category === "ActiveSquad") {
      hasActiveSquad = true;
    } else if (ownedOrEvolvedFilters?.category) {
      newSearchParams.set("ownedOrEvolved", ownedOrEvolvedFilters.category);
    }

    stableQueryParams.forEach((param) => {
      const key = Object.keys(param)[0];
      const values = param[key];
      if (values?.length > 0) newSearchParams.set(key, values.join(","));
    });

    let finalSearchString = newSearchParams.toString();
    if (hasActiveSquad) {
      finalSearchString = finalSearchString
        ? `${finalSearchString}&activeSquad`
        : "activeSquad";
    }

    setSearchParams(new URLSearchParams(finalSearchString), {
      replace: true,
      preventScrollReset: true,
    });
  }, [
    hasInitializedFromUrl,
    readyToSync,
    playerRole,
    playerRoleIds,
    sort,
    ownedOrEvolvedFilters,
    stableQueryParams,
    setSearchParams,
  ]);

  return { hasInitializedFromUrl };
};
